import { useQuery, useMutation } from "@tanstack/react-query";
import { config } from "../config";

const BASE_URL = config.BackendUrl;

// 🔹 Exported Fetch Function with timeout
export const fetcher = async (endpoint) => {
  const controller = new AbortController();
  // Use shorter timeouts to prevent hanging
  const timeout = endpoint.includes("/reviews") ? 20000 : 10000;
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
      },
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Error fetching ${endpoint}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error(`Request timeout: ${endpoint}`);
    }
    throw error;
  }
};

// 🔹 Fetch doctor info
export const useDoctor = () =>
  useQuery({
    queryKey: ["doctor"],
    queryFn: () => fetcher("/doctor"),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// 🔹 Fetch About info
export const useAbout = () =>
  useQuery({
    queryKey: ["about"],
    queryFn: () => fetcher("/about/"),
    select: (data) => data.about,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// 🔹 Fetch contacts
export const useContacts = () =>
  useQuery({
    queryKey: ["contacts"],
    queryFn: () => fetcher("/contact"),
    select: (data) => data.contact,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// 🔹 Fetch footer details (treatments, references, site details)
export const useFooterData = () => {
  const treatments = useQuery({
    queryKey: ["treatments"],
    queryFn: () => fetcher("/treatments"),
    staleTime: 15 * 60 * 1000, // Keep data fresh for 15 minutes
    gcTime: 60 * 60 * 1000, // Cache data for 1 hour
    refetchOnMount: false, // Don't refetch on mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  const treatmentsRefs = useQuery({
    queryKey: ["treatmentReferences"],
    queryFn: () => fetcher("/treatmentReference"),
    staleTime: 15 * 60 * 1000, // Keep data fresh for 15 minutes
    gcTime: 60 * 60 * 1000, // Cache data for 1 hour
    refetchOnMount: false, // Don't refetch on mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  const siteDetails = useQuery({
    queryKey: ["siteDetails"],
    queryFn: () => fetcher("/siteDetails"),
    select: (data) => data.footer,
    staleTime: 15 * 60 * 1000, // Keep data fresh for 15 minutes
    gcTime: 60 * 60 * 1000, // Cache data for 1 hour
    refetchOnMount: false, // Don't refetch on mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  return { treatments, treatmentsRefs, siteDetails };
};

// 🔹 Fetch treatment by name
export const useTreatmentByName = (name) =>
  useQuery({
    queryKey: ["treatment", name],
    queryFn: () => fetcher(`/treatments/${encodeURIComponent(name)}`),
    enabled: !!name,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// 🔹 Fetch treatment references only
export const useTreatmentRefs = () =>
  useQuery({
    queryKey: ["treatmentRefs"],
    queryFn: () => fetcher("/treatmentReference"),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// 🔹 Fetch images by treatment name
export const useImagesByTreatmentName = (treatmentName) =>
  useQuery({
    queryKey: ["images", treatmentName],
    queryFn: () =>
      fetcher(
        `/treatmentImagesRoute/${encodeURIComponent(treatmentName)}/images`
      ),
    enabled: !!treatmentName,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// 🔹 Fetch treatments by treatment reference
export const useTreatmentsByRef = (treatmentReference) =>
  useQuery({
    queryKey: ["treatmentsByRef", treatmentReference],
    queryFn: () =>
      fetcher(`/treatments/tree/${encodeURIComponent(treatmentReference)}`),
    enabled: !!treatmentReference,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// 🔹 Fetch reviews from Easy.co.il
export const useReviews = () =>
  useQuery({
    queryKey: ["reviews"],
    queryFn: () => fetcher("/reviews"),
    select: (data) => data.data, // Extract the reviews array from the response
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    retry: 0, // Don't retry if it fails - fallback will be used
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    refetchOnMount: false, // Don't refetch on every mount
    timeout: 20000, // 20 second timeout for React Query
  });

// 🔹 Handle email submission (Mutation)
export const useSendEmail = () =>
  useMutation({
    mutationFn: async (emailData) => {
      const response = await fetch(`${BASE_URL}/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailData),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Email sending failed.");
      }
      return "המייל נשלח בהצלחה! ניצור איתך קשר בהקדם";
    },
  });
